import { BusinessSectorTableResponse } from '../services/business-sector-table';
import { ConditionsAndTariffValues, CustomTableChunk } from '../types/add_commerce.types';
import { BankAccount } from '../types/business/request';
import { CurrencyEnum } from '../types/enums/currency.enum';
import { TariffRateType } from '../types/enums/tariff.enum';

export const getBankAccountsCurrencyToOperate = (bankAccounts: BankAccount[]) => {
  const hasBankAccount = bankAccounts && bankAccounts.length > 0;
  let currencyToOperate = '';

  let uyuAccount = false;
  let usdAccount = false;

  for (const bankAccount of bankAccounts!) {
    const currency = +bankAccount.accountCurrency;

    if (currency === CurrencyEnum.URUGUAYAN_PESOS) {
      uyuAccount = true;
    } else if (currency === CurrencyEnum.US_DOLLAR) {
      usdAccount = true;
    }
  }

  if (uyuAccount && usdAccount) {
    currencyToOperate = 'ambas';
  } else if (usdAccount) {
    currencyToOperate = '2';
  } else {
    currencyToOperate = '1';
  }

  return { hasBankAccount, currencyToOperate };
};

export const parsePersonalizedTariffsSentToNumbers = (
  tariffSent: ConditionsAndTariffValues['tariff'],
  personalizedData: { visa: CustomTableChunk[]; master: CustomTableChunk[] },
) => {
  if (!tariffSent) return personalizedData;

  const visaTariffRows = personalizedData.visa[0].rows;
  const masterTariffRows = personalizedData.master[0].rows;

  const personalizedMasterTariffSent = masterTariffRows.map((masterPlan) => {
    switch (masterPlan.plan) {
      case 'DÉBITO':
        masterPlan.currency = Number(tariffSent?.masterDebit?.replace('%', ''));
        return masterPlan;
      case 'PREPAGO':
        masterPlan.currency = Number(tariffSent?.masterPrepaid?.replace('%', ''));
        return masterPlan;
      case 'CRÉDITO CONTADO':
        masterPlan.currency = Number(tariffSent?.masterCashCredit?.replace('%', ''));
        return masterPlan;
      case 'CRÉDITO CUOTAS':
        masterPlan.currency = Number(tariffSent?.masterInstallmentCredit?.replace('%', ''));
        return masterPlan;
      case 'Extranjeros DÉBITO':
        masterPlan.currency = Number(tariffSent?.masterForeignDebit?.replace('%', ''));
        return masterPlan;
      case 'Extranjeros CRÉDITO':
        masterPlan.currency = Number(tariffSent?.masterForeignCredit?.replace('%', ''));
        return masterPlan;
      default:
        return masterPlan;
    }
  });

  const personalizedVisaTariffSent = visaTariffRows.map((visaPlan) => {
    switch (visaPlan.plan) {
      case 'DÉBITO':
        visaPlan.currency = Number(tariffSent?.visaDebit?.replace('%', ''));
        return visaPlan;
      case 'PREPAGO':
        visaPlan.currency = Number(tariffSent?.visaPrepaid?.replace('%', ''));
        return visaPlan;
      case 'CRÉDITO CONTADO':
        visaPlan.currency = Number(tariffSent?.visaCashCredit?.replace('%', ''));
        return visaPlan;
      case 'CRÉDITO CUOTAS':
        visaPlan.currency = Number(tariffSent?.visaInstallmentCredit?.replace('%', ''));
        return visaPlan;
      case 'Extranjeros DÉBITO':
        visaPlan.currency = Number(tariffSent?.visaForeignDebit?.replace('%', ''));
        return visaPlan;
      case 'Extranjeros CRÉDITO':
        visaPlan.currency = Number(tariffSent?.visaForeignCredit?.replace('%', ''));
        return visaPlan;
      default:
        return visaPlan;
    }
  });

  return {
    master: [{ rows: personalizedMasterTariffSent }],
    visa: [{ rows: personalizedVisaTariffSent }],
  };
};

export const updatePersonalizedTariffs = (
  personalizedTariffs: { visa: CustomTableChunk[]; master: CustomTableChunk[] },
  masterRateType: TariffRateType,
  visaRateType: TariffRateType,
) => {
  const personalizedMasterTariffSent = personalizedTariffs.master[0].rows.map((masterPlan) => {
    masterPlan.currency = masterRateType === TariffRateType.MINIMUM ? masterPlan.deferred : masterPlan.fees;
    return masterPlan;
  });

  const personalizedVisaTariffSent = personalizedTariffs.visa[0].rows.map((visaPlan) => {
    visaPlan.currency = visaRateType === TariffRateType.MINIMUM ? visaPlan.deferred : visaPlan.fees;
    return visaPlan;
  });

  return {
    master: [{ rows: personalizedMasterTariffSent }],
    visa: [{ rows: personalizedVisaTariffSent }],
  };
};

export const checkCanAccessPersonalizedTariff = (
  businessSector: string,
  managerHasTariffRestriction: boolean,
) => {
  let canAccessPersonalizedTariff = localStorage.getItem('canAccessPersonalizedTariff') === 'true';

  // if (businessSector === BusinessSectorEnum[BusinessSectorEnum.COMBUSTIBLES] && canAccessPersonalizedTariff) {
  //   canAccessPersonalizedTariff = !managerHasTariffRestriction;
  // }

  return canAccessPersonalizedTariff;
};

/**
 * Only executives explicitly flagged without tariff restriction (`tariffRestriction === false`,
 * set from the admin) can use the manual tariff. A missing value keeps it hidden.
 */
export const checkCanUseManualTariff = () => localStorage.getItem('tariffRestriction') === 'false';

type BrandTariffTables = { visa: CustomTableChunk[]; master: CustomTableChunk[] };

const cloneBrandTables = (tables: BrandTariffTables): BrandTariffTables => ({
  master: tables.master.map((chunk) => ({ ...chunk, rows: chunk.rows.map((row) => ({ ...row })) })),
  visa: tables.visa.map((chunk) => ({ ...chunk, rows: chunk.rows.map((row) => ({ ...row })) })),
});

/** Rates the personalized tariff applies for the given annual billing answer and rate types. */
export const getPersonalizedTariffsFromSector = (
  sectorTableData: BusinessSectorTableResponse,
  exceedsAnnualBilling: string,
  masterRateType: TariffRateType,
  visaRateType: TariffRateType,
): BrandTariffTables => {
  const source = exceedsAnnualBilling === 'true' ? sectorTableData.exceeds : sectorTableData.standard;
  return updatePersonalizedTariffs(cloneBrandTables(source), masterRateType, visaRateType);
};
